import { PostgrestError, SupabaseClient } from "@supabase/supabase-js";
import { type AllContactsRow } from "./audience-contacts";
import { Database } from "../../supabase.types";
import { logException } from "@punchup/sentry";
import { chunkArray } from "@punchup/utils/chunkArray";

type ContactListRow = Database["public"]["Tables"]["contact_lists"]["Row"];
type ContactRow = Database["public"]["Tables"]["contacts"]["Row"];
type FollowerRow =
  Database["public"]["Tables"]["comedian_dashboard_followers_details"]["Row"];

/** One of a comedian's imported contact lists, as the Lists page shows it. */
export interface ComedianContactList {
  id: ContactListRow["id"];
  name: ContactListRow["name"];
  /** Every contact on the list. */
  contactCount: number;
  /** Contacts with an email address. */
  emailCount: number;
  /** Contacts with a phone number. */
  phoneCount: number;
  /** Contacts with a location. */
  locationCount: number;
  createdAt: ContactListRow["created_at"];
}

/**
 * A list member: an All Contacts row plus a zip code and the country the
 * import stored. A member who follows the comedian shows the follower
 * snapshot's phone, zip code, city and state; every other member shows the
 * values the import stored. `id` is always the contact id, also on a `punchup`
 * row, because it is the list table's row key.
 */
export interface ContactListMember extends AllContactsRow {
  zipcode: string | null;
  country: string | null;
}

export interface ContactListMembersPage {
  rows: ContactListMember[];
  /** True when another page follows this one. */
  hasMore: boolean;
}

const LIST_COLUMNS =
  "id, name, num_contacts, email_contact_count, phone_contact_count, geo_contact_count, created_at";

type ListColumns = Pick<
  ContactListRow,
  | "id"
  | "name"
  | "num_contacts"
  | "email_contact_count"
  | "phone_contact_count"
  | "geo_contact_count"
  | "created_at"
>;

function toComedianContactList(list: ListColumns): ComedianContactList {
  return {
    id: list.id,
    name: list.name,
    contactCount: list.num_contacts ?? 0,
    emailCount: list.email_contact_count ?? 0,
    phoneCount: list.phone_contact_count ?? 0,
    locationCount: list.geo_contact_count ?? 0,
    createdAt: list.created_at,
  };
}

type ContactColumns = Pick<
  ContactRow,
  | "id"
  | "email"
  | "phone"
  | "zipcode"
  | "city"
  | "state"
  | "country"
  | "matched_user_id"
>;

const FOLLOWER_COLUMNS =
  "user_id, phone, zipcode, city, state, state_abbreviation";

type FollowerColumns = Pick<
  FollowerRow,
  "user_id" | "phone" | "zipcode" | "city" | "state" | "state_abbreviation"
>;

/** Keeps each `.in()` URL well under the request URL limit of the hosted API. */
const FOLLOWER_READ_CHUNK = 200;

function toContactListMember(
  addedAt: ContactListMember["addedAt"],
  contact: ContactColumns,
  follower: FollowerColumns | undefined
): ContactListMember {
  const member: ContactListMember = {
    id: contact.id,
    email: contact.email,
    phone: contact.phone,
    addedAt,
    city: contact.city,
    state: contact.state,
    // The audience view reads an imported contact's state as its abbreviation too.
    stateAbbreviation: contact.state,
    zipcode: contact.zipcode,
    country: contact.country,
    source: "imported",
  };
  if (!follower) return member;
  return {
    ...member,
    phone: follower.phone,
    zipcode: follower.zipcode,
    city: follower.city,
    state: follower.state,
    stateAbbreviation: follower.state_abbreviation,
    source: "punchup",
  };
}

/** Logs a failed read and throws, so the page never shows an empty table as fact. */
function failRead(
  error: PostgrestError,
  context: string,
  ids: { comedianId?: string; listId?: string }
): never {
  logException(error, { extra: { ...ids, context } });
  throw new Error(`Error in ${context}: ${error.message}`);
}

/**
 * Reads a comedian's imported contact lists, newest first. The counts are the
 * ones the import stores on each list. Throws on a database error rather than
 * returning no lists, because an empty table would read as fact.
 */
export async function getComedianContactLists(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<ComedianContactList[]> {
  const { data, error } = await supabase
    .from("contact_lists")
    .select(LIST_COLUMNS)
    .eq("comedian_id", comedianId)
    .order("created_at", { ascending: false });

  if (error) failRead(error, "getComedianContactLists", { comedianId });
  return (data ?? []).map(toComedianContactList);
}

/**
 * A token (`<listId>:<updated_at>`) for the comedian's newest import that the
 * near-venue counts do not include yet, or null when every import is in them.
 * Newest by `updated_at`, so a re-import into the same list is a new token.
 * Only finished imports (`created`) count: the hourly marking never flips a
 * `processing` row.
 */
export async function getPendingImportId(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<string | null> {
  const { data, error } = await supabase
    .from("contact_lists")
    .select("id, updated_at")
    .eq("comedian_id", comedianId)
    .eq("processed", false)
    .eq("status", "created")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) failRead(error, "getPendingImportId", { comedianId });
  return data ? `${data.id}:${data.updated_at}` : null;
}

/**
 * Reads one of a comedian's lists. Null when the list does not exist or
 * belongs to another comedian, so a URL cannot open someone else's list.
 */
export async function getComedianContactList(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  listId: string
): Promise<ComedianContactList | null> {
  const { data, error } = await supabase
    .from("contact_lists")
    .select(LIST_COLUMNS)
    .eq("comedian_id", comedianId)
    .eq("id", listId)
    .maybeSingle();

  if (error) failRead(error, "getComedianContactList", { comedianId, listId });
  return data ? toComedianContactList(data) : null;
}

/**
 * Reads the follower snapshot rows of the given users for one comedian, keyed
 * on user id. A user with no row does not follow the comedian now.
 */
async function getFollowerSnapshotRows(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  userIds: string[]
): Promise<Map<string, FollowerColumns>> {
  const results = await Promise.all(
    chunkArray(userIds, FOLLOWER_READ_CHUNK).map((chunk) =>
      supabase
        .from("comedian_dashboard_followers_details")
        .select(FOLLOWER_COLUMNS)
        .eq("comedian_id", comedianId)
        .in("user_id", chunk)
    )
  );

  const followers = new Map<string, FollowerColumns>();
  for (const { data, error } of results) {
    if (error) failRead(error, "getFollowerSnapshotRows", { comedianId });
    for (const follower of data) {
      followers.set(follower.user_id, follower);
    }
  }
  return followers;
}

/**
 * Reads the first `limit` contacts on a list, in the order they joined it, as
 * a page of list members. The join table, not the audience view, holds the
 * list's members: the view leaves out a contact who also subscribed. One row
 * past `limit` answers `hasMore` with no count query, as All Contacts does.
 *
 * A member matched to a Punchup user who follows the comedian shows the
 * follower snapshot's phone, zip code, city and state, as All Contacts does.
 * A snapshot row is the test of "follows": a Punchup user who does not follow
 * the comedian shows only the values the import stored.
 */
export async function getContactListContacts(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  listId: string,
  { limit }: { limit: number }
): Promise<ContactListMembersPage> {
  const { data, error } = await supabase
    .from("contact_lists_contacts")
    .select(
      "contact_id, created_at, contacts(id, email, phone, zipcode, city, state, country, matched_user_id)"
    )
    .eq("list_id", listId)
    .order("created_at", { ascending: true })
    .order("contact_id", { ascending: true })
    .limit(limit + 1);

  if (error) failRead(error, "getContactListContacts", { comedianId, listId });
  const members = data ?? [];
  const hasMore = members.length > limit;
  const page = members
    .slice(0, limit)
    .map(({ contact_id, created_at, contacts: contact }) => {
      if (!contact) {
        throw new Error(
          `List ${listId} member ${contact_id} has no contact row`
        );
      }
      return { addedAt: created_at, contact };
    });

  const matchedUserIds = Array.from(
    new Set(
      page.flatMap(({ contact }) =>
        contact.matched_user_id ? [contact.matched_user_id] : []
      )
    )
  );
  const followers = await getFollowerSnapshotRows(
    supabase,
    comedianId,
    matchedUserIds
  );

  const rows = page.map(({ addedAt, contact }) =>
    toContactListMember(
      addedAt,
      contact,
      contact.matched_user_id
        ? followers.get(contact.matched_user_id)
        : undefined
    )
  );
  return { rows, hasMore };
}
